import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AvailableStation } from '../../interface/station-management.interface';
import { useAppDispatch } from '../../redux/hooks';
import { updateSingleStation } from '../../redux/slice/ApprovedStationManagementSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import CustomToggleButton from '../../shared-components/custom-toggle-button/CustomToggleButton';
import DisableSwitchWarning from '../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../shared-components/switch-button-field/SwitchButton';
import stationApi from '../../utils/api/station';

import ApprovedList from './components/ApprovedList';
import PendingList from './components/PendingList';
import RejectedList from './components/RejectedList';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
  closeModal: () => void;
}

/**
 * Station management page
 */
function StationManagement() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { page } = useParams<{ page: string }>();
  const { t } = useTranslation('stationManagement');
  const [openTab, setOpenTab] = useState<number>(0);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
    /**
     * Close modal
     */
    closeModal: () => setModalBoxStatus('close'),
  });

  /**
   * Confirm the status change function
   * @param id
   * @param checked
   */
  const confirmStatusChange = (id: string, checked: boolean) => {
    stationApi
      .editAvailableStatusStation(id, { availabilityStatus: checked })
      .then((response) => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          content: (
            <SuccessNotification
              title={t('statusChangedTitle')}
              description={t('statusChangedDescription')}
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                dispatch(updateSingleStation(response.data));
              }}
            />
          ),
          notificationModal: true,
          header: false,
          /**
           * Close modal with function to dispatch station details
           */
          closeModal: () => {
            setModalBoxStatus('close');
            dispatch(updateSingleStation(response.data));
          },
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {AvailableDevice} device - The device to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (device: AvailableStation, checked: boolean) => {
    if (checked) {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: t('disableWarningTitle'),
        content: (
          <DisableSwitchWarning
            selectedId={device._id}
            onCancel={() => setModalBoxStatus('close')}
            message={t('disableWarningMessage')}
            onConfirm={() => confirmStatusChange(device._id, checked)}
          />
        ),
        notificationModal: true,
        header: false,
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
    } else {
      confirmStatusChange(device._id, checked);
    }
  };

  /**
   * Renders the custom data for the availability column
   * @param {AvailableDevice} device - The device data
   * @returns {JSX.Element} The rendered availability toggle cell
   */
  const renderAvailability = (device: AvailableStation) => (
    <div
      role="presentation"
      className="flex justify-center"
      onClick={(event) => event.stopPropagation()} // 🔥 prevents row click
      onMouseDown={(event) => event.stopPropagation()} // extra safety
    >
      <div role="presentation" key={device.id} onClick={(even) => even.stopPropagation()}>
        <SwitchButton
          checked={device.isAvailable}
          change={(checked: boolean) => handleAvailabilityChange(device, checked)}
        />
      </div>
    </div>
  );

  /**
   * Handle the change the page
   * @param index
   */
  const changePageHandle = (index: number) => {
    setOpenTab(index);
    let pageName = '';
    switch (index) {
      case 0:
        pageName = 'approved';
        break;
      case 1:
        pageName = 'pending';
        break;
      case 2:
        pageName = 'rejected';
        break;

      default:
        break;
    }
    navigate(`../${pageName}`); // ✅ React Router method
  };

  useEffect(() => {
    switch (page) {
      case 'approved':
        setOpenTab(0);
        break;
      case 'pending':
        setOpenTab(1);
        break;
      case 'rejected':
        setOpenTab(2);
        break;
      default:
        setOpenTab(0);
        break;
    }
  }, [page]);

  useEffect(() => {
    if (searchParameters.get('stationId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="station-management-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={modalBoxDetail.closeModal}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="tabs">
        <CustomToggleButton
          key={openTab} 
          labels={[t('approved'), t('pending'), t('rejected')]}
          onToggle={changePageHandle}
          defaultActiveIndex={openTab}
        />
      </div>
      <div className="table-container">
        {openTab === 0 && <ApprovedList renderAvailability={renderAvailability} />}
        {openTab === 1 && <PendingList />}
        {openTab === 2 && <RejectedList />}
      </div>
    </div>
  );
}

export default StationManagement;
